import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Users, UserPlus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { formatPropertyRef } from "./utils";

const CRMLeads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", source: "website", notes: "",
    property_interest: "", budget_min: "", budget_max: ""
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchLeads();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/crm/leads`, {
        withCredentials: true
      });
      setLeads(response.data || []);
    } catch (error) {
      console.error("Error fetching leads:", error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/leads`, {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null
      }, { withCredentials: true });
      toast.success("Lead created!");
      setDialogOpen(false);
      setFormData({ name: "", email: "", phone: "", source: "website", notes: "", property_interest: "", budget_min: "", budget_max: "" });
      fetchLeads();
    } catch (error) {
      toast.error("Failed to create lead");
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/crm/leads/${leadId}?status=${status}`, {}, {
        withCredentials: true
      });
      fetchLeads();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    qualified: "bg-green-100 text-green-700",
    negotiating: "bg-purple-100 text-purple-700",
    won: "bg-emerald-100 text-emerald-700",
    lost: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif">Leads</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Source</label>
                <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Budget Min</label>
                  <Input
                    type="number"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Budget Max</label>
                  <Input
                    type="number"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full">Create Lead</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-sm p-4 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 bg-muted rounded-sm">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif text-xl mb-2">No Leads Yet</h3>
          <p className="text-muted-foreground">Add your first lead to start tracking.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <div key={lead.lead_id} className="bg-card border border-border rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium">{lead.name}</h3>
                  <p className="text-sm text-muted-foreground">{lead.email}</p>
                  {lead.phone && <p className="text-sm text-muted-foreground">{lead.phone}</p>}
                  {(lead.property_ref || lead.property_title || lead.property_interest) && (
                    <div className="mt-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      <span className="text-sm">
                        <Badge variant="outline" className="mr-2 font-mono text-xs bg-primary/5">
                          {formatPropertyRef(lead.property_ref)}
                        </Badge>
                        {lead.property_title && (
                          <span className="text-muted-foreground">{lead.property_title}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <Select value={lead.status} onValueChange={(v) => updateStatus(lead.lead_id, v)}>
                  <SelectTrigger className={`w-32 h-8 text-xs ${statusColors[lead.status]}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="negotiating">Negotiating</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                <span>Source: {lead.source}</span>
                {lead.budget_min && lead.budget_max && (
                  <span>Budget: K{lead.budget_min.toLocaleString()} - K{lead.budget_max.toLocaleString()}</span>
                )}
              </div>
              {lead.notes && (
                <p className="mt-2 text-sm text-muted-foreground bg-muted p-2 rounded">{lead.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CRMLeads;
